import axios, { AxiosRequestConfig } from 'axios';
import {
  attachRequestId,
  noteApiResponse,
  rememberFailedRequest,
  tagBodyWithRequestId,
} from './requestId';
import {
  renewSignInAndResend,
  SignInRenewalFailed,
  signOutLocally,
} from './renewSignIn';

// Default 5-minute timeout covers normal endpoints comfortably. Long-running
// endpoints (file uploads, document generation, etc) set their own larger
// timeouts per-request, which take precedence over this default. Without
// any timeout, a hung backend leaves the UI spinning forever — picking up a
// floor here means the user gets a clear failure instead of an infinite wait.
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Every call carries an X-Request-ID the server logs on every line, so an
// error can name the call that failed (ZLAYT-1519).
axiosInstance.interceptors.request.use(attachRequestId);

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosInstance.interceptors.request.use(
  async (config: any) => {
    // Absent key parsed to {}, which is truthy, so requests made before sign
    // in went out as `Bearer [object Object]` and were rejected as bad
    // credentials rather than as anonymous. A malformed value parsed to
    // whatever it happened to be. Only a non-empty string is a token.
    let accessToken: unknown = null;
    try {
      const stored = localStorage.getItem('accessToken');
      accessToken = stored ? JSON.parse(stored) : null;
    } catch {
      accessToken = null;
    }

    if (typeof accessToken === 'string' && accessToken.length > 0) {
      const newConfig = { ...config };
      newConfig.headers.Authorization = `Bearer ${accessToken}`;
      return newConfig;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response: any) => {
    noteApiResponse(response);
    return response;
  },
  async (error: any) => {
    noteApiResponse(error?.response);
    rememberFailedRequest(error);
    // This instance's callers get the response body, not the axios error.
    const rejectWith = (failure: any) =>
      Promise.reject(
        tagBodyWithRequestId(
          (failure?.response && failure.response.data) || 'Wrong Services',
          failure
        )
      );
    const toSignIn = () => {
      if (!window.location.href.includes('/sign-in')) {
        window.location.pathname = '/sign-in';
      }
    };
    // An expired sign-in is renewed and the request resent, as in
    // ApiHandler; this instance used to send every 401 to sign-in, so the
    // use-case and deposition summary screens signed users out at expiry
    // (ZLAYT-1649).
    let resent;
    try {
      resent = await renewSignInAndResend(error);
    } catch (failure: any) {
      if (failure instanceof SignInRenewalFailed) {
        signOutLocally();
        toSignIn();
        return rejectWith(error);
      }
      // The resend failed for its own reason: a 401 still means sign-in.
      if (failure?.response?.status === 401) toSignIn();
      return rejectWith(failure);
    }
    if (resent) return resent;
    if (error.response?.status === 401) toSignIn();
    return rejectWith(error);
  }
);

export default axiosInstance;
export const baseAPIUrl = import.meta.env.VITE_APP_API_URL;

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};
